import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import {
  HOMEPAGE_DEFAULTS,
  deepMergeHomepage,
  toEmbedUrl,
  resolveMediaUrl
} from '../constants/homepageDefaults';
import '../styles/landing.css';

const Landing = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [content, setContent] = useState(deepMergeHomepage(HOMEPAGE_DEFAULTS));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${config.apiUrl}/site-content.php`, { timeout: 15000 });
        if (!cancelled && res.data?.content) {
          setContent(deepMergeHomepage(res.data.content));
        }
      } catch (err) {
        console.warn('Using default homepage content', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { brand, hero, demo, features, pricing, testimonials, cta, footer } = content;
  const logoSrc = resolveMediaUrl(brand.logoUrl);
  const videosWithUrl = (demo.videos || []).filter((v) => v.url && v.url.trim());

  const BrandMark = ({ className = '' }) => (
    <div className={`logo ${className}`}>
      {logoSrc ? (
        <img src={logoSrc} alt={brand.logoAlt || brand.productName} className="site-logo-img" />
      ) : (
        <span className="logo-icon">🛍️</span>
      )}
      <div className="logo-text-stack">
        <span className="logo-text">{brand.productName}</span>
        {brand.parentCompany && (
          <span className="logo-parent">by {brand.parentCompany}</span>
        )}
      </div>
    </div>
  );

  const CtaLink = ({ to, className, children }) => {
    if (!to) return null;
    if (to.startsWith('http') || to.startsWith('mailto:')) {
      return (
        <a href={to} className={className} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }
    if (to.startsWith('#')) {
      return (
        <a href={to} className={className}>
          {children}
        </a>
      );
    }
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  };

  return (
    <div className={`landing ${loading ? 'landing-loading' : ''}`}>
      <section className="hero">
        <nav className="navbar">
          <BrandMark />

          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>

          <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>
              Features
            </a>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>
              Pricing
            </a>
            {demo.enabled && (
              <a href="#demo" onClick={() => setIsMobileMenuOpen(false)}>
                Demo
              </a>
            )}
            <a href="#testimonials" onClick={() => setIsMobileMenuOpen(false)}>
              Testimonials
            </a>
            <Link to="/login" className="btn-login" onClick={() => setIsMobileMenuOpen(false)}>
              Login
            </Link>
            <Link to="/register" className="btn-signup" onClick={() => setIsMobileMenuOpen(false)}>
              Start Free Trial
            </Link>
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-text">
            {brand.tagline && <p className="hero-eyebrow">{brand.tagline}</p>}
            <h1>{hero.headline}</h1>
            <p>{hero.subheadline}</p>
            <div className="hero-buttons">
              <CtaLink to={hero.primaryCtaLink} className="btn-primary">
                {hero.primaryCta}
              </CtaLink>
              <CtaLink to={hero.secondaryCtaLink} className="btn-secondary">
                {hero.secondaryCta}
              </CtaLink>
            </div>
            <div className="hero-stats">
              {(hero.stats || []).map((stat, index) => (
                <div key={index} className="stat">
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-image">
            {hero.imageUrl ? (
              <img src={resolveMediaUrl(hero.imageUrl)} alt={`${brand.productName} ERP preview`} />
            ) : null}
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          <h2>{features.sectionTitle}</h2>
          <p className="section-subtitle">{features.sectionSubtitle}</p>

          <div className="features-grid">
            {(features.items || []).map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {demo.enabled && (
        <section id="demo" className="demo-section">
          <div className="container">
            <h2>{demo.sectionTitle}</h2>
            <p className="section-subtitle">{demo.sectionSubtitle}</p>

            {videosWithUrl.length === 0 ? (
              <p className="demo-empty">Demo videos will appear here once added by the admin.</p>
            ) : (
              <div className={`demo-grid ${videosWithUrl.length === 1 ? 'single' : ''}`}>
                {videosWithUrl.map((video, index) => {
                  const embed = toEmbedUrl(video.url);
                  return (
                    <article key={index} className="demo-card">
                      <div className="demo-embed">
                        {embed.endsWith('.mp4') ? (
                          <video src={embed} controls title={video.title} />
                        ) : (
                          <iframe
                            src={embed}
                            title={video.title || `Demo ${index + 1}`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        )}
                      </div>
                      {(video.title || video.description) && (
                        <div className="demo-meta">
                          {video.title && <h3>{video.title}</h3>}
                          {video.description && <p>{video.description}</p>}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      <section id="pricing" className="pricing">
        <div className="container">
          <h2>{pricing.sectionTitle}</h2>
          <p className="section-subtitle">{pricing.sectionSubtitle}</p>

          <div className="pricing-grid">
            {(pricing.plans || []).map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="price">
                  <span className="currency">KSh</span>
                  <span className="amount">{plan.price}</span>
                  <span className="period">/{plan.period}</span>
                </div>
                <ul className="features-list">
                  {(plan.features || []).map((feature, i) => (
                    <li key={i}>✓ {feature}</li>
                  ))}
                </ul>
                <Link to="/register" className={`btn-${plan.popular ? 'primary' : 'secondary'}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonials" className="testimonials">
        <div className="container">
          <h2>{testimonials.sectionTitle}</h2>
          <p className="section-subtitle">{testimonials.sectionSubtitle}</p>

          <div className="testimonials-grid">
            {(testimonials.items || []).map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="rating">
                  {'★'.repeat(testimonial.rating || 5)}
                  {'☆'.repeat(5 - (testimonial.rating || 5))}
                </div>
                <p className="content">&ldquo;{testimonial.content}&rdquo;</p>
                <div className="customer">
                  <div className="avatar">
                    {(testimonial.name || '?')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </div>
                  <div className="info">
                    <div className="name">{testimonial.name}</div>
                    <div className="business">{testimonial.business}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="container">
          <h2>{cta.headline}</h2>
          <p>{cta.subtext}</p>
          <CtaLink to={cta.buttonLink} className="btn-primary btn-large">
            {cta.buttonText}
          </CtaLink>
          {cta.footnote && <p className="no-credit-card">{cta.footnote}</p>}
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-col">
              <BrandMark />
              <p>{footer.about}</p>
              <div className="social-links">
                {footer.social?.facebook && (
                  <a href={footer.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
                    📘
                  </a>
                )}
                {footer.social?.twitter && (
                  <a href={footer.social.twitter} target="_blank" rel="noreferrer" aria-label="Twitter">
                    🐦
                  </a>
                )}
                {footer.social?.instagram && (
                  <a href={footer.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
                    📷
                  </a>
                )}
                {footer.social?.linkedin && (
                  <a href={footer.social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                    💼
                  </a>
                )}
                {footer.email && (
                  <a href={`mailto:${footer.email}`} aria-label="Email">
                    📧
                  </a>
                )}
              </div>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              {demo.enabled && <a href="#demo">Demo</a>}
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <Link to="/contact">Contact</Link>
              <span className="footer-muted">{brand.parentCompany}</span>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <Link to="/contact">Help Center</Link>
              <a href={`mailto:${footer.email}`}>Email Support</a>
            </div>
            <div className="footer-col">
              <h4>Contact</h4>
              {footer.phone && <p>📞 {footer.phone}</p>}
              {footer.email && <p>✉️ {footer.email}</p>}
              {footer.address && <p>📍 {footer.address}</p>}
            </div>
          </div>
          <div className="footer-bottom">
            <p>{footer.copyright}</p>
            <div className="footer-links">
              <a href="#features">Privacy Policy</a>
              <a href="#pricing">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
